import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock, Send, Trash2, RefreshCw } from "lucide-react";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rajib_admin_token";

export default function AdminComments() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [submitting, setSubmitting] = useState({});

  const fetchAll = async (overrideToken) => {
    const useToken = overrideToken || token;
    if (!useToken) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/comments`, {
        headers: { "X-Admin-Token": useToken },
      });
      setComments(data || []);
      setAuthed(true);
      localStorage.setItem(TOKEN_KEY, useToken);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        setError("Incorrect admin token.");
        setAuthed(false);
        localStorage.removeItem(TOKEN_KEY);
      } else {
        setError("Could not load. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submitLogin = (e) => {
    e.preventDefault();
    fetchAll();
  };

  const submitReply = async (id) => {
    const draft = (replyDrafts[id] || "").trim();
    if (!draft) return;
    setSubmitting((s) => ({ ...s, [id]: true }));
    try {
      await axios.post(
        `${API}/admin/comments/${id}/reply`,
        { adminReply: draft },
        { headers: { "X-Admin-Token": token } }
      );
      setReplyDrafts((d) => ({ ...d, [id]: "" }));
      await fetchAll();
    } catch (e) {
      logError("Failed to post admin reply:", e);
    } finally {
      setSubmitting((s) => ({ ...s, [id]: false }));
    }
  };

  const deleteComment = async (id) => {
    if (!window.confirm("Delete this comment? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/comments/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      await fetchAll();
    } catch (e) {
      logError("Failed to delete comment:", e);
      window.alert("Could not delete this comment. Please try again.");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-6 py-20" data-testid="admin-login">
        <motion.form
          onSubmit={submitLogin}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-7 sm:p-9"
        >
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#4A6B53]/10 text-[#4A6B53] mb-4">
            <Lock size={22} />
          </div>
          <h1 className="font-serif text-3xl text-[#2C2A28]">Admin sign-in</h1>
          <p className="mt-2 text-sm text-[#5C5852]">
            Enter your admin token to view and reply to visitor comments.
          </p>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="w-full mt-5 bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-4 py-3 text-[#2C2A28] field-ring"
            data-testid="admin-token-input"
            autoFocus
          />
          {error && <p className="mt-3 text-xs text-[#CC7A52]" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            className="mt-5 w-full px-4 py-3 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors text-sm tracking-wide"
            data-testid="admin-login-submit"
          >
            Sign in
          </button>
        </motion.form>
      </div>
    );
  }

  return (
    <div className="container-narrow py-12 sm:py-16" data-testid="admin-comments-page">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <p className="eyebrow">Admin · Comments</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
            Visitor notes.
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => fetchAll()}
            className="inline-flex items-center gap-1.5 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
            data-testid="admin-refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={() => {
              localStorage.removeItem(TOKEN_KEY);
              setToken("");
              setAuthed(false);
            }}
            className="text-sm text-[#5C5852] hover:text-[#CC7A52] transition-colors"
            data-testid="admin-signout"
          >
            Sign out
          </button>
        </div>
      </div>

      {loading && <p className="text-[#5C5852] italic">Loading…</p>}
      {!loading && comments.length === 0 && (
        <p className="text-[#5C5852] italic" data-testid="admin-empty">No comments yet.</p>
      )}

      <ul className="space-y-5">
        {comments.map((c) => (
          <li
            key={c.id}
            className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-6 sm:p-7"
            data-testid={`admin-comment-${c.id}`}
          >
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <p className="eyebrow !text-[10px] !text-[#CC7A52]">
                  {c.subjectLine}
                </p>
                <p className="mt-2 font-serif text-2xl text-[#2C2A28]">
                  {c.authorName}
                  {c.authorEmail && (
                    <span className="text-sm text-[#5C5852] font-sans italic ml-2">
                      &lt;{c.authorEmail}&gt;
                    </span>
                  )}
                </p>
              </div>
              <span className="text-xs text-[#5C5852]">
                {new Date(c.createdAt).toLocaleString()}
              </span>
            </div>
            <p className="mt-4 text-[#2C2A28] leading-relaxed whitespace-pre-line">
              {c.body}
            </p>

            {c.adminReply ? (
              <div className="mt-5 pt-4 border-t border-dashed border-[#D8D3CC]">
                <p className="eyebrow !text-[10px]">
                  Your reply{c.adminRepliedAt ? ` · ${new Date(c.adminRepliedAt).toLocaleDateString()}` : ""}
                </p>
                <p className="mt-1.5 text-[#2C2A28] italic whitespace-pre-line">
                  {c.adminReply}
                </p>
              </div>
            ) : (
              <div className="mt-5 pt-4 border-t border-dashed border-[#D8D3CC]">
                <textarea
                  rows={3}
                  value={replyDrafts[c.id] || ""}
                  onChange={(e) =>
                    setReplyDrafts((d) => ({ ...d, [c.id]: e.target.value }))
                  }
                  placeholder="Reply to this visitor…"
                  className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] field-ring resize-y"
                  data-testid={`admin-reply-input-${c.id}`}
                />
                <div className="mt-2 flex items-center justify-end gap-2">
                  <button
                    onClick={() => deleteComment(c.id)}
                    className="inline-flex items-center gap-1.5 text-xs text-[#CC7A52] hover:text-[#B36A47] transition-colors"
                    data-testid={`admin-delete-${c.id}`}
                  >
                    <Trash2 size={12} />
                    Delete
                  </button>
                  <button
                    onClick={() => submitReply(c.id)}
                    disabled={submitting[c.id] || !(replyDrafts[c.id] || "").trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors text-xs tracking-wide disabled:opacity-50"
                    data-testid={`admin-reply-submit-${c.id}`}
                  >
                    {submitting[c.id] ? "Sending…" : "Send reply"}
                    {!submitting[c.id] && <Send size={12} />}
                  </button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
